import React, { useState } from 'react';
import { Container, Button, Card, Modal } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { PlayCircle, Stethoscope, Handshake, ArrowRightLeft } from 'lucide-react';

import CrnaDemo from './CRNADemoPage'; // adjust path if needed
import GroupDemoPage from './GroupDemoPage'; // adjust path if needed

const FadeIn = ({ children, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.6 }}
  >
    {children}
  </motion.div>
);

const steps = [
  {
    number: 1,
    title: 'We Start with You',
    text: `Whether you're a CRNA or a Group, we begin with a one-on-one consultation.\nGroups outline their coverage needs. Providers share their goals, values, and what makes them thrive.\nIt's not just about shifts — it’s about fit.`,
    icon: <Handshake className="text-primary mb-2" size={32} />,
    color: '#007bff', // Blue
  },
  {
    number: 2,
    title: 'We Curate the Match',
    text: `Using what we’ve learned, RxRooster identifies alignments based on real preferences — not just availability.\nWe prioritize mutual interest, shared expectations, and long-term potential.`,
    icon: <ArrowRightLeft className="text-success mb-2" size={32} />,
    color: '#28a745', // Green
  },
  {
    number: 3,
    title: 'We Make the Introduction',
    text: `When we spot a strong fit, we connect the dots.\nThe conversation is yours to shape, with someone already aligned with your goals and values.`,
    icon: <Stethoscope className="text-danger mb-2" size={32} />,
    color: '#dc3545', // Red
  },
];

const DemoLandingPage = () => {
  const [showModal, setShowModal] = useState(false);
  const [activeStep, setActiveStep] = useState(null);

  const handleOpenModal = (step) => {
    setActiveStep(step);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setActiveStep(null);
  };

  return (
    <Container fluid className="my-5">
      {/* How It Works */}
      <div className="px-3 px-md-4">
        <FadeIn delay={0.4}>
          <Card className="shadow-sm border-0 p-5 rounded-4 text-center bg-light w-100" style={{ maxWidth: '100%' }}>
            <h2 className="mb-5 fw-semibold d-flex align-items-center justify-content-center gap-2 flex-wrap">
              How
              <img
                src="https://rxrooster.com/logo-no-rooster.png"
                alt="RxRooster Logo"
                style={{ width: '170px' }}
              />
              Works
            </h2>

            <div
              className="d-flex flex-column flex-md-row justify-content-evenly align-items-stretch gap-4"
              style={{ width: '100%' }}
            >
              {steps.map((step, i) => (
                <FadeIn key={i} delay={0.2 * (i + 1)}>
                  <div
                    className="text-center text-md-start px-md-3 position-relative"
                    style={{
                      flex: 1,
                      minWidth: 0,
                      maxWidth: '100%',
                    }}
                  >
                    {/* Step Number */}
                    <div
                      className="step-number pulse mx-auto mx-md-0 d-flex justify-content-center align-items-center mb-3"
                      style={{
                        width: '64px',
                        height: '64px',
                        borderRadius: '50%',
                        backgroundColor: step.color,
                        fontSize: '1.5rem',
                        fontWeight: 'bold',
                        color: '#fff',
                        animationDelay: `${i * 0.4}s`,
                      }}
                    >
                      {step.number}
                    </div>

                    {step.icon}
                    <h4 className="fw-bold mt-2">{step.title}</h4>
                    <p
                      className="text-muted"
                      style={{
                        whiteSpace: 'pre-wrap',
                        lineHeight: 1.6,
                        maxWidth: '100%',
                        margin: '0 auto',
                      }}
                    >
                      {step.text}
                    </p>

                    {/* Demo Button */}
                    {(i === 0 || i === 1) && (
                      <div className="mt-3">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="rounded-pill px-4 py-2 fw-medium demo-button"
                          onClick={() => handleOpenModal(i + 1)}
                        >
                          <PlayCircle className="me-1" size={14} />
                          Demo
                        </Button>
                      </div>
                    )}
                  </div>
                </FadeIn>
              ))}
            </div>

            {/* Logo */}
            <FadeIn delay={2.5}>
              <div className="text-center mt-5">
                <img
                  src="https://rxrooster.com/logo-rooster.png"
                  alt="RxRooster Logo"
                  style={{ width: '45px', opacity: 0.95 }}
                />
              </div>
            </FadeIn>
          </Card>
        </FadeIn>
      </div>

      {/* Step Demo Modal */}
      <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{activeStep === 2 ? 'Wake Up to Better CRNA Matches' : "Group Coverage Snapshots"}</Modal.Title>
        </Modal.Header>
        <Modal.Body
          style={{
            maxHeight: '75vh',
            overflowY: 'auto',
            paddingRight: '1rem',
          }}
        >
          {activeStep === 1 && (
            <div>
              <GroupDemoPage modal />
            </div>
          )}
          {activeStep === 2 && (
            <div>
              <CrnaDemo modal />
            </div>
          )}
          {activeStep === 3 && (
            <div>
              <h5 className="mb-3">Step 3: We Make the Introduction</h5>
              <p>
                When we identify a strong alignment, we make the intro. From there, it’s your relationship
                to shape — already grounded in shared expectations.
              </p>
            </div>
          )}
        </Modal.Body> 


      </Modal>
    </Container>
  );
};

export default DemoLandingPage;
